import { isEmpty } from 'lodash';

import { TSchedule, TScheduleTemplate } from './types';

/**
 * Режим работы в интерфейсе.
 *
 * Бэк знает только два типа графика — `Schedule::WEEKDAY_TYPE` (0) и
 * `Schedule::DAY_BY_DAY_TYPE` (1), и с 14.09.2026 `ScheduleUpdateRequest` их
 * жёстко проверяет через `Rule::in`. «Каждый день» — не третий тип, а пресет
 * над недельным: неделя, в которой ни один день не выходной.
 */
export type TScheduleMode = 'week' | 'interval' | 'daily';

/** Значения `schedule_type` из модели `Schedule` — только они уходят на сервер. */
export const SCHEDULE_TYPE = {
    WEEKDAY: 0,
    DAY_BY_DAY: 1,
} as const;

/**
 * До этой правки фронт отправлял «Каждый день» как `schedule_type: 2`. Бэк такой
 * тип не принимал никогда (`schedule_days` там обязателен и приходил пустым), но
 * часть графиков успела сохраниться с двойкой через PUT, пока тот не проверял
 * значение. Такие записи читаем как `daily` и чиним при первом же сохранении.
 */
const LEGACY_DAILY_TYPE = 2;

const WEEK_DAYS_COUNT = 7;

export const SCHEDULE_TYPE_BY_MODE: Record<TScheduleMode, number> = {
    week: SCHEDULE_TYPE.WEEKDAY,
    interval: SCHEDULE_TYPE.DAY_BY_DAY,
    daily: SCHEDULE_TYPE.WEEKDAY,
};

/** Рабочих и выходных дней при выборе режима: с этого набора начинается таблица дней. */
export const DEFAULT_DAYS_BY_MODE: Record<TScheduleMode, { on: number; off: number }> = {
    week: { on: 5, off: 2 },
    interval: { on: 2, off: 2 },
    daily: { on: WEEK_DAYS_COUNT, off: 0 },
};

/**
 * Режим по данным графика. `daily` не хранится отдельным полем — это неделя,
 * где все семь дней рабочие, поэтому и вычисляется из состава дней.
 *
 * Пустой `schedule_days` (свежая форма создания) осознанно не считается `daily`:
 * там дни ещё не разложены, режим по умолчанию — недельный.
 */
export const getScheduleMode = (schedule?: TSchedule | TScheduleTemplate): TScheduleMode => {
    if (!schedule) return 'week';

    if (schedule.schedule_type === LEGACY_DAILY_TYPE) return 'daily';
    if (schedule.schedule_type === SCHEDULE_TYPE.DAY_BY_DAY) return 'interval';

    const isEveryDayWorking =
        isEmpty(schedule.day_offs) && schedule.schedule_days?.length === WEEK_DAYS_COUNT;

    return isEveryDayWorking ? 'daily' : 'week';
};
